import * as React from 'react';
import { t as translateMessage, type MessageKey, type TranslateOptions } from '@reos/i18n';

/**
 * Locale vrstva web-adminu (krok I1, CLAUDE.md §5: jazyk je atribut
 * uzivatele/tenantu, ne globalni nastaveni aplikace). Zdroj pravdy pro
 * preklady je `@reos/i18n` katalog (`webAdmin.*` v cs.json/en.json); tenhle
 * modul jen resi, KTERY locale se ma pouzit v danem UI stromu.
 */
export type UiLocale = 'cs' | 'en';

export const SUPPORTED_UI_LOCALES: readonly UiLocale[] = ['cs', 'en'];
const DEFAULT_UI_LOCALE: UiLocale = 'cs';

function isUiLocale(value: string | undefined): value is UiLocale {
  return value !== undefined && (SUPPORTED_UI_LOCALES as readonly string[]).includes(value);
}

/**
 * Odvozeny locale z uzivatele/tenantu podle CLAUDE.md §5 (user -> tenant -> cs).
 * ROZHODNUTI (krok I1, viz REPORT-step-I1.md): `SessionUser`/`TenantInfo`
 * (`stores/session.ts`) dnes nemaji pole `locale`/`defaultLocale` — web-admin
 * session neni napojena na endpoint, ktery by je posilal (Nalez). Az pribudou,
 * dopise se sem cteni `user.locale ?? tenant.defaultLocale`; do te doby funkce
 * vraci vzdy vychozi `cs` a skutecny vyber pro dev/demo drzi `LocaleProvider`
 * ve vlastnim React state (prepinac v hlavicce), ne v session.
 */
function deriveLocaleFromSession(): UiLocale {
  return DEFAULT_UI_LOCALE;
}

interface LocaleContextValue {
  readonly locale: UiLocale;
  readonly setLocale: (locale: UiLocale) => void;
}

const LocaleContext = React.createContext<LocaleContextValue | null>(null);

/**
 * Provider locale kontextu. Drzi dev/demo prepinac (cs/en) ve vlastnim state
 * misto session — viz ROZHODNUTI u `deriveLocaleFromSession`. Zabaluje se
 * kolem `<Outlet />` v `components/layout.tsx`, takze prezije navigaci mezi
 * routami (Layout se pri zmene route neremountuje).
 *
 * `locale` prop je jen pro testy/storybook — pevne nastavi locale bez ohledu
 * na prepinac (nepouziva se v bezicí aplikaci, ktera locale odvozuje sama).
 */
export function LocaleProvider({
  children,
  locale: fixedLocale,
}: {
  readonly children: React.ReactNode;
  readonly locale?: UiLocale;
}): React.ReactElement {
  const [override, setOverride] = React.useState<UiLocale | null>(null);
  const locale = fixedLocale ?? override ?? deriveLocaleFromSession();
  const setLocale = React.useCallback((next: UiLocale) => {
    setOverride(isUiLocale(next) ? next : null);
  }, []);
  const value = React.useMemo<LocaleContextValue>(() => ({ locale, setLocale }), [locale, setLocale]);
  return React.createElement(LocaleContext.Provider, { value }, children);
}

function useLocaleContext(): LocaleContextValue {
  const ctx = React.useContext(LocaleContext);
  if (ctx === null) throw new Error('useLocale/useT/useSetLocale musi byt pouzity uvnitr <LocaleProvider>.');
  return ctx;
}

/** Aktualni locale UI stromu (viz `LocaleProvider`). */
export function useLocale(): UiLocale {
  return useLocaleContext().locale;
}

/**
 * Locale pro SDILENE komponenty, ktere se renderuji i v panelech bez
 * `LocaleProvider` (krok UXF3: `DateField` sedi i v `AnswerQuestionPanel`
 * a v `rent.tsx`, ktere prekladaji pres `t(key, { locale })`). Mimo provider
 * vraci vychozi `cs` misto vyjimky — `useLocale()` zustava strict, aby se
 * obrazovky dal musely do provideru zabalit.
 */
export function useLocaleOrDefault(): UiLocale {
  return React.useContext(LocaleContext)?.locale ?? DEFAULT_UI_LOCALE;
}

/** Dev/demo prepinac jazyka — mimo `LocaleProvider` neni k dispozici jina cesta, jak locale zmenit. */
export function useSetLocale(): (locale: UiLocale) => void {
  return useLocaleContext().setLocale;
}

/**
 * ICU hodnoty pro `useT()`. `values`/`locale` jsou tu zakazane klice —
 * `t()` z `useT()` bere hodnoty PRIMO (2. argument), na rozdil od `t()`
 * z `@reos/i18n`, ktery bere `{ locale, values }`. Vada 54 (KATALOG-VAD.md):
 * `t(key, { values: { count } })` volane na `useT()` typecheck neodhalil
 * (values byl `Record<string, unknown>`) a padalo az za behu.
 */
export type UseTValues = Readonly<Record<string, unknown>> & { values?: never; locale?: never };

/** `t()` z `@reos/i18n` navazane na aktualni locale — komponenty nemusi locale predavat rucne. */
export function useT(): (key: MessageKey, values?: UseTValues) => string {
  const { locale } = useLocaleContext();
  return React.useCallback(
    (key: MessageKey, values?: UseTValues) => translateMessage(key, { locale, values: values as TranslateOptions['values'] }),
    [locale],
  );
}
